import React, {useContext, useEffect, useState} from 'react';
import classnames from 'classnames';
import {Link, RouteComponentProps, withRouter} from 'react-router-dom';
import {Tabs, Tab} from '@ellucian/react-design-system/core';

import {PathDesignContext} from './path-design-context';

import 'stylesheets/components/shared-routed-tabs.scss';

export interface Props {
  tabs: {
    label: React.ReactNode;
    onClick?: () => void;
    path: string;
    skipScrolling?: boolean;
    queryString?: string;
  }[];
  className?: string;
  basePath?: string;
  replaceOnNavigation?: boolean;
}

const RoutedTabs = ({
  tabs,
  location: {pathname},
  match: {url},
  history: {push},
  className,
  basePath,
  replaceOnNavigation = true,
}: Props & RouteComponentProps) => {
  const {enabled} = useContext(PathDesignContext);
  const [activeTab, setActiveTab] = useState(0);
  const updatedUrl = basePath ? basePath : url;
  const allPaths = tabs.map(({path}) => `${updatedUrl}${path}`);

  useEffect(() => {
    const activeIndex = allPaths.reduce((bestIndex, path, index) => {
      const matches = pathname === path || pathname.startsWith(`${path}/`);
      if (!matches) return bestIndex;

      return bestIndex === -1 || path.length > allPaths[bestIndex].length ? index : bestIndex;
    }, -1);

    setActiveTab(activeIndex);
  }, [allPaths, pathname]);

  if (enabled) {
    return (
      <Tabs
        value={activeTab}
        data-testid="routed-tabs"
        classes={{root: 'shared-routed-tabs-outer--path'}}
      >
        {tabs.map(({label, onClick, path, skipScrolling, queryString}) => (
          <Tab
            key={path}
            label={label}
            onClick={() => {
              push({
                pathname: `${updatedUrl}${path}`,
                state: {skipScrolling},
                search: queryString ? queryString : undefined,
              });
              onClick?.();
            }}
            data-testid="routed-tab"
          />
        ))}
      </Tabs>
    );
  }

  return (
    <div className="shared-routed-tabs-outer" data-testid="routed-tabs">
      <div className={classnames('shared-routed-tabs', className)} role="tablist">
        {tabs.map(({label, onClick, path, skipScrolling, queryString}) => {
          const fullPath = `${updatedUrl}${path}`;
          const active = pathname === fullPath;

          return (
            <Link
              key={path}
              className={classnames('shared-routed-tabs-tab', {active})}
              aria-selected={active}
              role="tab"
              replace={replaceOnNavigation}
              to={{
                pathname: fullPath,
                state: {skipScrolling},
                search: queryString ? queryString : undefined,
              }}
              onClick={onClick}
              data-testid="routed-tab"
            >
              {label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default withRouter(RoutedTabs);
